/**
 * Style to Template Migration
 * Converts existing StyleEclipse/Split/Type styles to template JSON format
 */

import { nanoid } from 'nanoid';
import type { Template, TemplateVariable, CanvasBackground } from '@/types/template';
import type { Layer, TextLayer, PrimitiveLayer } from '@/types/layers';
import { createAlignmentPosition, createConstraintPosition } from '@/types/position';
import { solid } from '@/types/brush';

// Color constants from existing styles
const COLORS = {
  BG_SPACE: '#0a0e17',
  BG_NAVY: '#111428',
  FG: '#EDEDED',
  GLOW: '#FFE17D',
  ACCENT: '#6366F1',
};

// Default template variables
const DEFAULT_VARIABLES: TemplateVariable[] = [
  {
    id: 'headline',
    name: 'Headline',
    type: 'text',
    defaultValue: 'Your Privacy is Priceless',
    required: true,
  },
  {
    id: 'subhead',
    name: 'Subhead',
    type: 'text',
    defaultValue: "Don't let data brokers profit from your personal information",
    required: false,
  },
  {
    id: 'cta',
    name: 'Call to Action',
    type: 'text',
    defaultValue: 'Start Free Trial',
    required: true,
  },
];

/**
 * Create the Eclipse style template
 */
export function createEclipseTemplate(): Template {
  const layers: Layer[] = [
    // Star field (background)
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Star Field',
      position: createAlignmentPosition('TopLeft'),
      visible: true,
      locked: true,
      opacity: 1,
      content: {
        primitiveType: 'star-field',
        starCount: 150,
      },
      responsiveOverrides: {
        tiny: { visible: true },
        small: { visible: true },
      },
    } as PrimitiveLayer,

    // Eclipse moon
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Eclipse Moon',
      position: createAlignmentPosition('TopCenter', 0, 80),
      width: 320,
      height: 320,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'eclipse-moon',
        size: 320,
      },
      responsiveOverrides: {
        tiny: {
          position: createAlignmentPosition('CenterLeft', 24, 0),
          width: 64,
          height: 64,
        },
        small: {
          position: createAlignmentPosition('TopCenter', 0, 20),
          width: 120,
          height: 120,
        },
        wide: {
          position: createAlignmentPosition('CenterLeft', 96, 0),
          width: 360,
          height: 360,
        },
        tall: {
          position: createAlignmentPosition('TopCenter', 0, 140),
          width: 340,
          height: 340,
        },
      },
    } as PrimitiveLayer,

    // Wordmark/Logo
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Wordmark',
      position: createAlignmentPosition('TopCenter', 0, 440),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'wordmark',
        size: 24,
      },
      responsiveOverrides: {
        tiny: { visible: false },
        small: { visible: false },
        wide: {
          position: createConstraintPosition(
            { type: 'right', offset: 96 },
            { type: 'center', offset: -120 }
          ),
        },
      },
    } as PrimitiveLayer,

    // Headline
    {
      id: nanoid(),
      type: 'text',
      name: 'Headline',
      position: createAlignmentPosition('Center', 0, 60),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        text: '{{headline}}',
        variableBinding: '{{headline}}',
        fontSize: 48,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 700,
        color: solid(COLORS.FG),
        textAlign: 'center',
        lineHeight: 1.08,
        letterSpacing: -0.022,
      },
      responsiveOverrides: {
        tiny: {
          position: createAlignmentPosition('Center', 0, 0),
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 16,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: solid(COLORS.FG),
            textAlign: 'left',
            lineHeight: 1.15,
            letterSpacing: -0.01,
          },
        },
        small: {
          position: createAlignmentPosition('BottomCenter', 0, -60),
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 18,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: solid(COLORS.FG),
            textAlign: 'center',
            lineHeight: 1.15,
            letterSpacing: -0.01,
          },
        },
        wide: {
          position: createConstraintPosition(
            { type: 'right', offset: 96 },
            { type: 'center', offset: -60 }
          ),
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 58,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: solid(COLORS.FG),
            textAlign: 'left',
            lineHeight: 1.08,
            letterSpacing: -0.022,
          },
        },
        tall: {
          position: createAlignmentPosition('Center', 0, 80),
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 56,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: solid(COLORS.FG),
            textAlign: 'center',
            lineHeight: 1.08,
            letterSpacing: -0.022,
          },
        },
      },
    } as TextLayer,

    // Subhead
    {
      id: nanoid(),
      type: 'text',
      name: 'Subhead',
      position: createAlignmentPosition('Center', 0, 130),
      visible: true,
      locked: false,
      opacity: 0.65,
      content: {
        text: '{{subhead}}',
        variableBinding: '{{subhead}}',
        fontSize: 18,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        color: solid(COLORS.FG),
        textAlign: 'center',
        lineHeight: 1.45,
        maxWidth: 500,
      },
      responsiveOverrides: {
        tiny: { visible: false },
        small: { visible: false },
        wide: {
          position: createConstraintPosition(
            { type: 'right', offset: 96 },
            { type: 'center', offset: 20 }
          ),
          content: {
            text: '{{subhead}}',
            variableBinding: '{{subhead}}',
            fontSize: 20,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            color: solid(COLORS.FG),
            textAlign: 'left',
            lineHeight: 1.45,
            maxWidth: 560,
          },
        },
      },
    } as TextLayer,

    // CTA Button
    {
      id: nanoid(),
      type: 'primitive',
      name: 'CTA Button',
      position: createAlignmentPosition('BottomCenter', 0, -120),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'cta-button',
        text: '{{cta}}',
        variableBinding: '{{cta}}',
        props: {
          variant: 'glow',
          size: 'lg',
        },
      },
      responsiveOverrides: {
        tiny: {
          position: createAlignmentPosition('CenterRight', -24, 0),
          content: {
            primitiveType: 'cta-button',
            text: '{{cta}}',
            variableBinding: '{{cta}}',
            props: { variant: 'glow', size: 'sm' },
          },
        },
        small: {
          position: createAlignmentPosition('BottomCenter', 0, -18),
          content: {
            primitiveType: 'cta-button',
            text: '{{cta}}',
            variableBinding: '{{cta}}',
            props: { variant: 'glow', size: 'sm' },
          },
        },
        wide: {
          position: createConstraintPosition(
            { type: 'right', offset: 96 },
            { type: 'center', offset: 100 }
          ),
        },
      },
    } as PrimitiveLayer,

    // App Store Badges
    {
      id: nanoid(),
      type: 'primitive',
      name: 'App Badges',
      position: createAlignmentPosition('BottomCenter', 0, -50),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'app-badges',
        scale: 1,
      },
      responsiveOverrides: {
        tiny: { visible: false },
        small: { visible: false },
        tall: {
          content: {
            primitiveType: 'app-badges',
            scale: 1.1,
          },
        },
      },
    } as PrimitiveLayer,
  ];

  const background: CanvasBackground = {
    type: 'radial-gradient',
    centerX: 50,
    centerY: 40,
    stops: [
      { color: COLORS.BG_NAVY, position: 0 },
      { color: '#0d1020', position: 45 },
      { color: COLORS.BG_SPACE, position: 100 },
    ],
  };

  return {
    version: '2.0' as const,
    metadata: {
      id: 'eclipse',
      name: 'Eclipse',
      description: 'Dark space theme with glowing eclipse moon',
      category: 'brand',
      isBuiltin: true,
    },
    canvas: {
      baseWidth: 1080,
      baseHeight: 1080,
      background,
    },
    layers,
    variables: DEFAULT_VARIABLES,
  };
}

/**
 * Create the Split style template
 */
export function createSplitTemplate(): Template {
  const layers: Layer[] = [
    // Star field
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Star Field',
      position: createAlignmentPosition('TopLeft'),
      visible: true,
      locked: true,
      opacity: 1,
      content: {
        primitiveType: 'star-field',
        starCount: 100,
      },
    } as PrimitiveLayer,

    // Left side - Moon
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Eclipse Moon',
      position: createConstraintPosition(
        { type: 'left', offset: 60 },
        { type: 'center', offset: 0 }
      ),
      width: 280,
      height: 280,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'eclipse-moon',
        size: 280,
      },
      responsiveOverrides: {
        tiny: { visible: false },
        small: {
          position: createAlignmentPosition('TopCenter', 0, 20),
          width: 100,
          height: 100,
        },
        tall: {
          position: createAlignmentPosition('TopCenter', 0, 80),
          width: 240,
          height: 240,
        },
      },
    } as PrimitiveLayer,

    // Wordmark
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Wordmark',
      position: createConstraintPosition(
        { type: 'right', offset: 80 },
        { type: 'top', offset: 80 }
      ),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'wordmark',
        size: 22,
      },
      responsiveOverrides: {
        tiny: { visible: false },
        small: { visible: false },
      },
    } as PrimitiveLayer,

    // Headline
    {
      id: nanoid(),
      type: 'text',
      name: 'Headline',
      position: createConstraintPosition(
        { type: 'right', offset: 80 },
        { type: 'center', offset: -40 }
      ),
      width: 400,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        text: '{{headline}}',
        variableBinding: '{{headline}}',
        fontSize: 42,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 700,
        color: solid(COLORS.FG),
        textAlign: 'left',
        lineHeight: 1.1,
        letterSpacing: -0.02,
      },
      responsiveOverrides: {
        tiny: {
          position: createAlignmentPosition('Center'),
          width: undefined,
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 14,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: solid(COLORS.FG),
            textAlign: 'center',
            lineHeight: 1.2,
          },
        },
        small: {
          position: createAlignmentPosition('Center', 0, 20),
          width: undefined,
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 20,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: solid(COLORS.FG),
            textAlign: 'center',
            lineHeight: 1.15,
          },
        },
        tall: {
          position: createAlignmentPosition('Center', 0, 60),
          width: 280,
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 36,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: solid(COLORS.FG),
            textAlign: 'center',
            lineHeight: 1.12,
          },
        },
      },
    } as TextLayer,

    // CTA
    {
      id: nanoid(),
      type: 'primitive',
      name: 'CTA Button',
      position: createConstraintPosition(
        { type: 'right', offset: 80 },
        { type: 'center', offset: 60 }
      ),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'cta-button',
        text: '{{cta}}',
        variableBinding: '{{cta}}',
        props: { variant: 'glow', size: 'md' },
      },
      responsiveOverrides: {
        tiny: {
          position: createAlignmentPosition('CenterRight', -16, 0),
          content: {
            primitiveType: 'cta-button',
            text: '{{cta}}',
            variableBinding: '{{cta}}',
            props: { variant: 'glow', size: 'sm' },
          },
        },
        small: {
          position: createAlignmentPosition('BottomCenter', 0, -20),
        },
        tall: {
          position: createAlignmentPosition('BottomCenter', 0, -100),
        },
      },
    } as PrimitiveLayer,
  ];

  return {
    version: '2.0' as const,
    metadata: {
      id: 'split',
      name: 'Split',
      description: 'Split layout with moon on one side, copy on the other',
      category: 'brand',
      isBuiltin: true,
    },
    canvas: {
      baseWidth: 1200,
      baseHeight: 628,
      background: {
        type: 'linear-gradient',
        angle: 135,
        stops: [
          { color: COLORS.BG_NAVY, position: 0 },
          { color: COLORS.BG_SPACE, position: 100 },
        ],
      },
    },
    layers,
    variables: DEFAULT_VARIABLES,
  };
}

/**
 * Create the Type style template (typographic focus)
 */
export function createTypeTemplate(): Template {
  const layers: Layer[] = [
    // Subtle star field
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Star Field',
      position: createAlignmentPosition('TopLeft'),
      visible: true,
      locked: true,
      opacity: 0.5,
      content: {
        primitiveType: 'star-field',
        starCount: 60,
      },
    } as PrimitiveLayer,

    // Large headline
    {
      id: nanoid(),
      type: 'text',
      name: 'Headline',
      position: createAlignmentPosition('Center', 0, -40),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        text: '{{headline}}',
        variableBinding: '{{headline}}',
        fontSize: 72,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 800,
        color: solid(COLORS.FG),
        textAlign: 'center',
        lineHeight: 1,
        letterSpacing: -0.03,
        textTransform: 'uppercase',
      },
      responsiveOverrides: {
        tiny: {
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 18,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 800,
            color: solid(COLORS.FG),
            textAlign: 'center',
            lineHeight: 1.1,
            letterSpacing: -0.02,
            textTransform: 'uppercase',
          },
        },
        small: {
          content: {
            text: '{{headline}}',
            variableBinding: '{{headline}}',
            fontSize: 28,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 800,
            color: solid(COLORS.FG),
            textAlign: 'center',
            lineHeight: 1.05,
            letterSpacing: -0.025,
            textTransform: 'uppercase',
          },
        },
      },
    } as TextLayer,

    // Accent line
    {
      id: nanoid(),
      type: 'shape',
      name: 'Accent Line',
      position: createAlignmentPosition('Center', 0, 30),
      width: 80,
      height: 4,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        shapeType: 'rectangle',
        fill: solid(COLORS.GLOW),
        borderRadius: 2,
      },
      responsiveOverrides: {
        tiny: { visible: false },
        small: {
          width: 40,
          height: 3,
        },
      },
    },

    // CTA
    {
      id: nanoid(),
      type: 'primitive',
      name: 'CTA Button',
      position: createAlignmentPosition('BottomCenter', 0, -80),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        primitiveType: 'cta-button',
        text: '{{cta}}',
        variableBinding: '{{cta}}',
        props: { variant: 'outline', size: 'lg' },
      },
      responsiveOverrides: {
        tiny: {
          position: createAlignmentPosition('BottomCenter', 0, -12),
          content: {
            primitiveType: 'cta-button',
            text: '{{cta}}',
            variableBinding: '{{cta}}',
            props: { variant: 'outline', size: 'sm' },
          },
        },
        small: {
          position: createAlignmentPosition('BottomCenter', 0, -20),
          content: {
            primitiveType: 'cta-button',
            text: '{{cta}}',
            variableBinding: '{{cta}}',
            props: { variant: 'outline', size: 'md' },
          },
        },
      },
    } as PrimitiveLayer,

    // Wordmark at bottom
    {
      id: nanoid(),
      type: 'primitive',
      name: 'Wordmark',
      position: createAlignmentPosition('BottomCenter', 0, -30),
      visible: true,
      locked: false,
      opacity: 0.6,
      content: {
        primitiveType: 'wordmark',
        size: 18,
      },
      responsiveOverrides: {
        tiny: { visible: false },
        small: { visible: false },
      },
    } as PrimitiveLayer,
  ];

  return {
    version: '2.0' as const,
    metadata: {
      id: 'type',
      name: 'Type',
      description: 'Bold typographic design with minimal elements',
      category: 'brand',
      isBuiltin: true,
    },
    canvas: {
      baseWidth: 1080,
      baseHeight: 1080,
      background: solid(COLORS.BG_SPACE),
    },
    layers,
    variables: DEFAULT_VARIABLES,
  };
}

/**
 * Create a blank template with no layers
 */
export function createBlankTemplate(): Template {
  return {
    version: '2.0' as const,
    metadata: {
      id: 'blank',
      name: 'Blank',
      description: 'Empty canvas to start from scratch',
      category: 'custom',
      isBuiltin: false,
    },
    canvas: {
      baseWidth: 1080,
      baseHeight: 1080,
      background: solid(COLORS.BG_SPACE),
    },
    layers: [],
    variables: [],
  };
}
